import { CURRENCIES, type Currency } from '@fos/shared/contracts/common';
import {
  type OfferContentDto,
  type OfferContentPatch,
  type OfferDetail,
  updateOfferDraftInput,
} from '@fos/shared/contracts/offers';
import { OFFER_FIELD_LABELS } from '@fos/shared/labels';
import { type FormEvent, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import {
  amountInputValue,
  hoursInputValue,
  issuesToErrors,
  parseAmountInput,
  parseHoursInput,
  zodIssues,
} from '../../maintenance/form-utils';

const TEXT_SECTIONS = ['scope', 'result', 'exclusions', 'assumptions'] as const;

/** Prośba o wysyłkę tej treści czeka (albo jest zatwierdzona) w Akceptacjach — zmiana treści ją wycofa. */
export function isSendWaiting(offer: Pick<OfferDetail, 'pendingSend'>): boolean {
  const p = offer.pendingSend;
  return p !== null && !p.stale && (p.status === 'pending' || p.status === 'approved');
}

const HINTS: Partial<Record<string, string>> = {
  scope: 'Co zrobimy. Markdown, np. listy „- ”.',
  result: 'Co klient dostanie na końcu.',
  exclusions: 'Czego oferta nie obejmuje.',
  assumptions: 'Na czym opieramy wycenę.',
  terms: 'Rozliczenie, płatność, zmiany zakresu.',
  examples: 'Przykłady bez stałych cen wszystkich usług.',
};

const FIELD_KEYS = [
  'title',
  'content.scope',
  'content.result',
  'content.exclusions',
  'content.assumptions',
  'content.estimatedSeconds',
  'content.package',
  'content.rate',
  'content.currency',
  'content.timeline',
  'content.terms',
  'content.examples',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

interface FormState {
  title: string;
  scope: string;
  result: string;
  exclusions: string;
  assumptions: string;
  mode: 'estimate' | 'package';
  hours: string;
  packageText: string;
  rate: string;
  currency: Currency;
  timeline: string;
  terms: string;
  examples: string;
}

function initial(title: string, c: OfferContentDto): FormState {
  return {
    title,
    scope: c.scope,
    result: c.result,
    exclusions: c.exclusions,
    assumptions: c.assumptions,
    mode: c.package ? 'package' : 'estimate',
    hours: hoursInputValue(c.estimatedSeconds),
    packageText: c.package ?? '',
    rate: amountInputValue(c.rate ?? null),
    currency: c.currency,
    timeline: c.timeline ?? '',
    terms: c.terms,
    examples: c.examples ?? '',
  };
}

/**
 * Edycja roboczej wersji oferty na stronie oferty (zamiast treści; widok czeka na projekt w Figmie): tytuł, sekcje
 * Markdown, szacowane godziny albo pakiet, stawka i waluta (tylko z „Widzi stawki i kwoty”), termin, warunki,
 * przykłady. Wysyła tylko zmienione pola; stawka, której osoba nie widzi, zostaje bez zmian.
 */
export function OfferEditor({ offer, onDone }: { offer: OfferDetail; onDone: () => void }) {
  const access = useAccess();
  const seesRates = access.flag('seesRates');
  const utils = trpc.useUtils();
  const update = trpc.offers.updateDraft.useMutation();
  const current = offer.versions.find((v) => v.versionNo === offer.currentVersion);
  const base = current?.content;
  const [form, setForm] = useState<FormState | null>(() => (base ? initial(offer.title, base) : null));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);
  // zgoda na wysyłkę dotyczy treści z chwili prośby: zapis zmian wycofuje czekającą wysyłkę (Akceptacje: „Wycofana”)
  const sendWaiting = isSendWaiting(offer);

  if (!form || !base) return null;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const hours = parseHoursInput(form.hours);
    if (hours === undefined) {
      setErrors({ 'content.estimatedSeconds': 'Podaj liczbę godzin, np. 40 albo 12,5' });
      return;
    }
    let rate: string | null | undefined;
    if (seesRates) {
      rate = parseAmountInput(form.rate);
      if (rate === undefined) {
        setErrors({ 'content.rate': 'Podaj stawkę, np. 150 albo 150,50' });
        return;
      }
    }
    const patch: OfferContentPatch = {};
    for (const key of [...TEXT_SECTIONS, 'terms'] as const) {
      if (form[key] !== base[key]) patch[key] = form[key];
    }
    const examples = form.examples.trim() ? form.examples : null;
    if (examples !== (base.examples ?? null)) patch.examples = examples;
    const timeline = form.timeline.trim() || null;
    if (timeline !== (base.timeline ?? null)) patch.timeline = timeline;
    if ((hours ?? null) !== base.estimatedSeconds) patch.estimatedSeconds = hours ?? null;
    const pkg = form.mode === 'package' ? form.packageText.trim() || 'Pakiet godzin' : null;
    if (pkg !== (base.package ?? null)) patch.package = pkg;
    if (seesRates) {
      const baseRate = base.rate ? amountInputValue(base.rate) : '';
      if ((rate ? amountInputValue(rate) : '') !== baseRate) patch.rate = rate ?? null;
      if (form.currency !== base.currency) patch.currency = form.currency;
    }
    const title = form.title.trim() !== offer.title ? form.title : undefined;
    if (Object.keys(patch).length === 0 && title === undefined) {
      onDone();
      return;
    }
    const input = {
      id: offer.id,
      version: offer.version,
      ...(title !== undefined ? { title } : {}),
      ...(Object.keys(patch).length > 0 ? { content: patch } : {}),
    };
    const parsed = updateOfferDraftInput.safeParse(input);
    if (!parsed.success) {
      setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
      return;
    }
    try {
      await update.mutateAsync(input);
      toast.success(
        sendWaiting
          ? 'Zapisano ofertę. Prośba o wysyłkę została wycofana — wyślij ofertę ponownie.'
          : 'Zapisano ofertę',
      );
      await utils.offers.invalidate();
      onDone();
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const fieldErrors = issuesToErrors(validationIssues(err), FIELD_KEYS);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setFormError(
        code === 'CONFLICT' ? `${errorMessage(err)} Odśwież stronę, żeby zobaczyć aktualną ofertę.` : errorMessage(err),
      );
    }
  };

  const textField = (key: 'scope' | 'result' | 'exclusions' | 'assumptions' | 'terms' | 'examples', rows = 4) => (
    <Field key={key} label={OFFER_FIELD_LABELS[key]} hint={HINTS[key]} error={errors[`content.${key}`]}>
      <Textarea rows={rows} value={form[key]} onChange={(e) => set(key, e.target.value)} />
    </Field>
  );

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-5" aria-label="Edycja oferty">
      {sendWaiting && (
        <ContextBanner tone="info">
          Wysyłka tej wersji czeka w Akceptacjach. Zapisanie zmian wycofa tę prośbę — potem wyślesz ofertę ponownie.
        </ContextBanner>
      )}
      <Field label="Tytuł" error={errors.title}>
        <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
      </Field>
      {TEXT_SECTIONS.map((k) => textField(k, k === 'scope' ? 6 : 3))}

      <div className="flex flex-col gap-3 rounded-xl border border-line p-4">
        <Segmented
          aria-label="Rozliczenie"
          value={form.mode}
          onValueChange={(v) => set('mode', v)}
          options={[
            { value: 'estimate', label: 'Szacowane godziny' },
            { value: 'package', label: 'Pakiet godzin' },
          ]}
          className="self-start"
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label={form.mode === 'package' ? 'Godzin w pakiecie' : 'Szacowany czas (h)'}
            error={errors['content.estimatedSeconds']}
          >
            <Input mono inputMode="decimal" value={form.hours} onChange={(e) => set('hours', e.target.value)} />
          </Field>
          {seesRates && (
            <>
              <Field label="Stawka netto / h" error={errors['content.rate']}>
                <Input mono inputMode="decimal" value={form.rate} onChange={(e) => set('rate', e.target.value)} />
              </Field>
              <Field label="Waluta" error={errors['content.currency']}>
                <Select
                  value={form.currency}
                  onValueChange={(v) => set('currency', v as Currency)}
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                />
              </Field>
            </>
          )}
        </div>
        {form.mode === 'package' && (
          <Field label="Pakiet" error={errors['content.package']}>
            <Input
              value={form.packageText}
              onChange={(e) => set('packageText', e.target.value)}
              placeholder="np. miesięcznie, niewykorzystane godziny przechodzą na kolejny miesiąc"
            />
          </Field>
        )}
        <Field label={OFFER_FIELD_LABELS.timeline} error={errors['content.timeline']}>
          <Input
            value={form.timeline}
            onChange={(e) => set('timeline', e.target.value)}
            placeholder="np. ok. 6 tygodni od akceptacji"
          />
        </Field>
      </div>

      {textField('terms', 3)}
      {textField('examples', 3)}

      {formError && <FormMessage>{formError}</FormMessage>}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={update.isPending}>
          Anuluj
        </Button>
        <Button variant="primary" type="submit" loading={update.isPending}>
          Zapisz wersję {offer.currentVersion}
        </Button>
      </div>
    </form>
  );
}
